import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Palette, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const HEX = /^#[0-9A-Fa-f]{6}$/;
const NEUTRAL = { primary_colour: '#111111', secondary_colour: '#555555', logo_url: '' };
const errMsg = (e, fb) => e?.response?.data?.detail?.error?.message || e?.response?.data?.detail?.message || e?.response?.data?.error?.message || fb;

/** Day 24F-C — organisation-scoped Agreement branding (existing 24F-B store). Neutral black/white/grey when unset; never platform branding. */
export const OrganisationBrandingCard = () => {
  const [b, setB] = useState(null);
  const [form, setForm] = useState(NEUTRAL);
  const [file, setFile] = useState(null);
  const [clearLogo, setClearLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/admin/settings/branding/organisation'); const d = r.data?.data || {}; setB(d); setForm({ primary_colour: d.primary_colour || NEUTRAL.primary_colour, secondary_colour: d.secondary_colour || NEUTRAL.secondary_colour, logo_url: d.logo_url || '' }); }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e, 'Could not load organisation branding')); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const pick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!['image/png', 'image/jpeg'].includes(f.type)) { toast.error('Logo must be a PNG or JPEG image'); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error('Logo exceeds the 2MB limit'); return; }
    setFile(f); setClearLogo(false);
  };
  const save = async () => {
    if (!HEX.test(form.primary_colour) || !HEX.test(form.secondary_colour)) { toast.error('Colours must be 6-digit hex values, e.g. #1A73E8'); return; }
    setSaving(true);
    try {
      const fd = new FormData(); fd.append('primary_colour', form.primary_colour); fd.append('secondary_colour', form.secondary_colour);
      if (file) fd.append('logo', file); if (clearLogo) fd.append('clear_logo', 'true');
      await api.put('/admin/settings/branding/organisation', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Organisation branding saved'); setFile(null); setClearLogo(false); await load();
    } catch (e) { toast.error(errMsg(e, 'Could not save organisation branding')); } finally { setSaving(false); }
  };
  if (!b) return null;
  const logoSrc = file ? URL.createObjectURL(file) : (!clearLogo && form.logo_url ? `${API_BASE}${form.logo_url}` : null);
  const configured = b.configured && (b.logo_url || b.primary_colour !== NEUTRAL.primary_colour || b.secondary_colour !== NEUTRAL.secondary_colour);
  return (
    <Card data-testid="org-branding-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Palette className="h-4 w-4" /> Organisation branding for Agreement documents</CardTitle>
        <CardDescription>Logo and colours printed on this organisation's Service Agreements only. When nothing is configured, documents use a neutral black / white / grey style with no logo — platform branding is never applied.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {[['primary_colour', 'Primary colour (headings)'], ['secondary_colour', 'Secondary colour (rules / accents)']].map(([k, label]) => (
              <div className="space-y-1" key={k}><Label>{label}</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={HEX.test(form[k]) ? form[k] : '#000000'} onChange={(e) => setForm({ ...form, [k]: e.target.value.toUpperCase() })} className="h-9 w-12 rounded border" aria-label={label} />
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`font-mono ${HEX.test(form[k]) ? '' : 'border-red-400'}`} data-testid={`org-branding-${k}`} />
                </div>
              </div>))}
            <div className="space-y-1"><Label>Logo (PNG or JPEG, max 2MB)</Label>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick} data-testid="org-branding-logo-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="org-branding-logo-pick"><Upload className="h-3.5 w-3.5 mr-1" />{file ? file.name : 'Choose logo'}</Button>
                {(form.logo_url || file) && <Button type="button" variant="ghost" size="sm" onClick={() => { setFile(null); setClearLogo(true); }} data-testid="org-branding-logo-clear"><X className="h-3.5 w-3.5 mr-1" />Remove logo</Button>}
              </div>
            </div>
            <Button onClick={save} disabled={saving} data-testid="org-branding-save">{saving ? 'Saving…' : 'Save organisation branding'}</Button>
          </div>
          <div className="rounded-md border bg-white p-4 space-y-2" data-testid="org-branding-preview">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Document preview {configured || file ? '' : '— neutral (no branding configured)'}</div>
            {logoSrc ? <img src={logoSrc} alt="Organisation logo" className="max-h-12 object-contain" data-testid="org-branding-preview-logo" /> : <div className="text-xs text-muted-foreground italic" data-testid="org-branding-preview-nologo">No logo</div>}
            <div className="text-lg font-semibold" style={{ color: HEX.test(form.primary_colour) ? form.primary_colour : '#111111' }}>Service Agreement</div>
            <div className="border-b-2" style={{ borderColor: HEX.test(form.secondary_colour) ? form.secondary_colour : '#555555' }} />
            <p className="text-xs text-slate-700">Participant details · Schedule of Supports · Signatures</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
